import config, { apiClient, tokenManager } from '../config/apiConfig';
import { DEFAULT_PAGE_SIZE } from '../config/appConstants';

// Authentication API calls
export const authAPI = {
  login: async (userType, username, password) => {
    const response = await apiClient.post(config.ENDPOINTS.AUTH.LOGIN, {
      userType: userType,
      nameOrEmail: username,
      password: password
    });

    // Save JWT token if present in response
    if (response.data?.token) {
      tokenManager.setToken(response.data.token);
    }

    return response.data;
  },

  logout: async (userType, userId) => {
    const response = await apiClient.post(config.ENDPOINTS.AUTH.LOGOUT, {
      userType: userType,
      userId: userId
    });

    // Clear JWT token on logout
    tokenManager.removeToken();

    return response.data;
  },

  refreshToken: async () => {
    const response = await apiClient.post(config.ENDPOINTS.AUTH.REFRESH);

    // Update JWT token if present in response
    if (response.data?.token) {
      tokenManager.setToken(response.data.token);
    }

    return response.data;
  }
};

// Rides API
export const ridesAPI = {
  create: async (rideData) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.CREATE, rideData);
    return response.data;
  },

  getAll: async () => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_ALL);
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_BY_ID, {
      params: { id }
    });
    return response.data;
  },

  getAssigned: async () => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_ASSIGNED);
    return response.data;
  },

  getInProgress: async () => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_IN_PROGRESS);
    return response.data;
  },

  getFuture: async () => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_FUTURE);
    return response.data;
  },

  getToday: async () => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_TODAY);
    return response.data;
  },

  getOpen: async () => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_OPEN);
    return response.data;
  },

  getThisWeek: async () => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_THIS_WEEK);
    return response.data;
  },

  getAllHistory: async () => {
    const response = await apiClient.get(config.ENDPOINTS.RIDES.GET_ALL_HISTORY);
    return response.data;
  },

  /**
   * Gets paginated ride history for better performance with large datasets.
   * @param {number} page - Page number (1-based)
   * @param {number} pageSize - Number of rides per page (default: 50)
   * @returns {Promise<{rides: Array, pagination: {currentPage, pageSize, totalCount, totalPages, hasNextPage, hasPreviousPage}}>}
   */
  getAllHistoryPaginated: async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
    const response = await apiClient.get(
      `${config.ENDPOINTS.RIDES.GET_ALL_HISTORY_PAGINATED}?page=${page}&pageSize=${pageSize}`
    );
    return response.data;
  },

  /**
   * Gets filtered and paginated ride history.
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<{rides: Array, pagination: object}>}
   */
  getFilteredRidesPaginated: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.driverId) queryParams.append('driverId', params.driverId);
    if (params.customerName) queryParams.append('customerName', params.customerName);
    if (params.customerNumber) queryParams.append('customerNumber', params.customerNumber);
    if (params.rideStatus) queryParams.append('rideStatus', params.rideStatus);
    if (params.paidStatus) queryParams.append('paidStatus', params.paidStatus);
    if (params.baseFareMin != null && params.baseFareMin !== '') queryParams.append('baseFareMin', params.baseFareMin);
    if (params.baseFareMax != null && params.baseFareMax !== '') queryParams.append('baseFareMax', params.baseFareMax);

    const response = await apiClient.get(
      `${config.ENDPOINTS.RIDES.GET_FILTERED_RIDES_PAGINATED}?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Finds which page a specific ride is on in the paginated history.
   * @param {number} rideId - The ride ID to search for
   * @param {number} pageSize - Page size (default: 50)
   * @returns {Promise<{rideId: number, pageNumber: number, found: boolean}>}
   */
  findRidePage: async (rideId, pageSize = DEFAULT_PAGE_SIZE) => {
    const response = await apiClient.get(
      `${config.ENDPOINTS.RIDES.FIND_RIDE_PAGE}?rideId=${rideId}&pageSize=${pageSize}`
    );
    return response.data;
  },

  /**
   * Gets paginated rides for a specific week.
   * @param {Date|string} weekStart - Start date of the week (Sunday)
   * @param {number} page - Page number (1-based)
   * @param {number} pageSize - Number of rides per page (default: 50)
   * @returns {Promise<{rides: Array, weekStart: string, pagination: object}>}
   */
  getRidesForWeek: async (weekStart, page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
    // Format date as ISO string (YYYY-MM-DD)
    const dateStr = weekStart instanceof Date
      ? weekStart.toISOString().split('T')[0]
      : new Date(weekStart).toISOString().split('T')[0];
    const response = await apiClient.get(
      `${config.ENDPOINTS.RIDES.GET_RIDES_FOR_WEEK}?weekStart=${dateStr}&page=${page}&pageSize=${pageSize}`
    );
    return response.data;
  },

  /**
   * Finds which week and page a specific ride is on.
   * Used for search in weekly view mode.
   * @param {number} rideId - The ride ID to search for
   * @param {number} pageSize - Page size (default: 50)
   * @returns {Promise<{rideId: number, weekStart: string|null, pageNumber: number, found: boolean}>}
   */
  findRideWeekAndPage: async (rideId, pageSize = DEFAULT_PAGE_SIZE) => {
    const response = await apiClient.get(
      `${config.ENDPOINTS.RIDES.FIND_RIDE_WEEK_AND_PAGE}?rideId=${rideId}&pageSize=${pageSize}`
    );
    return response.data;
  },

  update: async (id, rideData) => {
    const response = await apiClient.put(`${config.ENDPOINTS.RIDES.UPDATE}/${id}`, rideData);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.RIDES.DELETE}/${id}`);
    return response.data;
  },

  assign: async (assignData) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.ASSIGN, assignData);
    return response.data;
  },

  cancel: async (cancelData) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.CANCEL, cancelData);
    return response.data;
  },

  cancelRecurring: async (rideId) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.CANCEL_RECURRING, rideId);
    return response.data;
  },

  cancelRecurringWithOption: async (rideId, keepCurrentRide) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.CANCEL_RECURRING_WITH_OPTION, {
      RideId: rideId,
      KeepCurrentRide: keepCurrentRide
    });
    return response.data;
  },

  reassign: async (reassignData) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.REASSIGN, reassignData);
    return response.data;
  },

  pickup: async (pickupData) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.PICKUP, pickupData);
    return response.data;
  },

  dropoff: async (dropoffData) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.DROPOFF, dropoffData);
    return response.data;
  },

  updateStatus: async (statusData) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.UPDATE_STATUS, statusData);
    return response.data;
  },

  calculatePrice: async (priceRequest) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.CALCULATE_PRICE, priceRequest);
    return response.data;
  },
  calculateStopsCost: async ({ pickup, dropOff, stops }) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.CALCULATE_STOPS_COST, { pickup, dropOff, stops });
    return response.data;
  },
  resetPickup: async (resetData) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.RESET_PICKUP, resetData);
    return response.data;
  },
  updatePrice: async (rideId, amount, driversComp) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.UPDATE_PRICE, {
      RideId: rideId,
      Amount: amount,
      DriversComp: driversComp
    });
    return response.data;
  },

  /**
   * Set paid status for a ride (dispatcher use)
   * @param {number} rideId - The ride ID
   * @param {boolean} paid - Whether the ride is paid
   * @param {string} paymentType - The payment type (required if paid=true): 'cash', 'zelle', 'dispatcherCC', 'other'
   * @param {string|null} paymentTokenId - Optional newly-collected card token (ccof:...) to charge for Card on File
   */
  setPaidStatus: async (rideId, paid, paymentType, paymentTokenId = null, amount = null) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.SET_PAID_STATUS, {
      RideId: rideId,
      Paid: paid,
      PaymentType: paymentType,
      PaymentTokenId: paymentTokenId,
      Amount: amount
    });
    return response.data;
  },

  /**
   * Dispatcher marks a ride as complete (sets DropOffTime, CompletedAt, Completed=true, charges card if needed)
   * @param {number} rideId
   */
  dispatcherCompleteRide: async (rideId) => {
    const response = await apiClient.post(`${config.ENDPOINTS.RIDES.DISPATCHER_COMPLETE_RIDE}/${rideId}`);
    return response.data;
  },

  /**
   * Dispatcher un-completes a ride, reverting to PickedUp (never modifies payment fields)
   * @param {number} rideId
   */
  dispatcherUncompleteRide: async (rideId) => {
    const response = await apiClient.post(`${config.ENDPOINTS.RIDES.DISPATCHER_UNCOMPLETE_RIDE}/${rideId}`);
    return response.data;
  },

  sendReceipt: async (rideId, email) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.SEND_RECEIPT, { rideId, email: email || null });
    return response.data;
  }
};

// Drivers API
export const driversAPI = {
  getAll: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DRIVERS.GET_ALL);
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`${config.ENDPOINTS.DRIVERS.GET_BY_ID}?id=${id}`);
    return response.data;
  },

  getActive: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DRIVERS.GET_ACTIVE);
    return response.data;
  },

  getDriving: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DRIVERS.GET_DRIVING);
    return response.data;
  },

  getDriversDriving: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DRIVERS.GET_DRIVERS_DRIVING);
    return response.data;
  },

  getOnlineDrivers: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DRIVERS.GET_ONLINE_DRIVERS);
    return response.data;
  },

  getTrackingLostAlerts: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DRIVERS.GET_TRACKING_LOST_ALERTS);
    return response.data;
  },

  create: async (driverData) => {
    const response = await apiClient.post(config.ENDPOINTS.DRIVERS.CREATE, driverData);
    return response.data;
  },

  update: async (driverData) => {
    const response = await apiClient.post(config.ENDPOINTS.DRIVERS.UPDATE, driverData);
    return response.data;
  },

  getDriverStatus: async (driverId) => {
    const response = await apiClient.get(config.ENDPOINTS.DRIVERS.GET_DRIVER_STATUS, {
      params: { userId: driverId }
    });
    return response.data;
  },

  updateStatus: async (driverId, status) => {
    const response = await apiClient.put(config.ENDPOINTS.DRIVERS.UPDATE_STATUS, {
      driverId,
      status
    });
    return response.data;
  },

  // Driver onboarding / applications
  invite: async (email) => {
    const response = await apiClient.post(config.ENDPOINTS.DRIVERS.INVITE, { email });
    return response.data;
  },

  getApplications: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DRIVERS.GET_APPLICATIONS);
    return response.data;
  },

  getApplicationById: async (id) => {
    const response = await apiClient.get(`${config.ENDPOINTS.DRIVERS.GET_APPLICATION_BY_ID}/${id}`);
    return response.data;
  },

  approveApplication: async (id) => {
    const response = await apiClient.post(`${config.ENDPOINTS.DRIVERS.APPROVE_APPLICATION}/${id}`);
    return response.data;
  },

  rejectApplication: async (id, reason) => {
    const response = await apiClient.post(`${config.ENDPOINTS.DRIVERS.REJECT_APPLICATION}/${id}`, { reason });
    return response.data;
  },

  resendInvite: async (id) => {
    const response = await apiClient.post(`${config.ENDPOINTS.DRIVERS.RESEND_INVITE}/${id}`);
    return response.data;
  },

  cancelInvite: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.DRIVERS.CANCEL_INVITE}/${id}`);
    return response.data;
  },

  getDocumentUrl: (driverId, type, version) => {
    const token = tokenManager.getToken();
    const base = `${apiClient.defaults.baseURL}${config.ENDPOINTS.DRIVERS.GET_DOCUMENT}/${driverId}/${type}`;
    const url = token ? `${base}?access_token=${encodeURIComponent(token)}` : base;
    return version ? `${url}${token ? '&' : '?'}v=${version}` : url;
  },

  uploadDocument: async (driverId, type, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await apiClient.post(
      `${config.ENDPOINTS.DRIVERS.UPLOAD_DOCUMENT}/${driverId}/${type}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  deleteDocument: async (driverId, type) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.DRIVERS.DELETE_DOCUMENT}/${driverId}/${type}`);
    return response.data;
  },

  revealSsn: async (driverId) => {
    const response = await apiClient.post(`${config.ENDPOINTS.DRIVERS.REVEAL_SSN}/${driverId}`);
    return response.data;
  },
};

// Dispatchers API
export const dispatchersAPI = {
  getActive: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DISPATCHERS.GET_ACTIVE);
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`${config.ENDPOINTS.DISPATCHERS.GET_BY_ID}?id=${id}`);
    return response.data;
  },

  create: async (dispatcherData) => {
    const response = await apiClient.post(config.ENDPOINTS.DISPATCHERS.CREATE, dispatcherData);
    return response.data;
  },

  update: async (dispatcherData) => {
    const response = await apiClient.post(config.ENDPOINTS.DISPATCHERS.UPDATE, dispatcherData);
    return response.data;
  },

  getPhotoUrl: (id, version) => {
    const token = tokenManager.getToken();
    const base = `${apiClient.defaults.baseURL}${config.ENDPOINTS.DISPATCHERS.GET_PHOTO}/${id}`;
    const url = token ? `${base}?access_token=${encodeURIComponent(token)}` : base;
    return version ? `${url}${token ? '&' : '?'}v=${version}` : url;
  },

  uploadPhoto: async (id, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await apiClient.post(
      `${config.ENDPOINTS.DISPATCHERS.UPLOAD_PHOTO}/${id}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  deletePhoto: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.DISPATCHERS.DELETE_PHOTO}/${id}`);
    return response.data;
  },

};

// Cars API
export const carsAPI = {
  getByDriver: async (driverId) => {
    const response = await apiClient.get(`${config.ENDPOINTS.CARS.GET_BY_DRIVER}?userId=${driverId}`);
    return response.data;
  },

  create: async (carData) => {
    const response = await apiClient.post(config.ENDPOINTS.CARS.CREATE, carData);
    return response.data;
  },

  dispatcherUpdate: async (carData) => {
    const response = await apiClient.post(config.ENDPOINTS.CARS.DISPATCHER_UPDATE, carData);
    return response.data;
  },

  disable: async (carId, reason) => {
    const response = await apiClient.post(config.ENDPOINTS.CARS.DISABLE, { carId, reason });
    return response.data;
  },

  enable: async (carId) => {
    const response = await apiClient.post(`${config.ENDPOINTS.CARS.ENABLE}/${carId}`);
    return response.data;
  },
};

// Messages API
export const messagesAPI = {
  send: async (messageData) => {
    const response = await apiClient.post(config.ENDPOINTS.MESSAGES.SEND_MESSAGE, messageData);
    return response.data;
  },

  getTodayMessages: async (driverId) => {
    const url = driverId
      ? `${config.ENDPOINTS.MESSAGES.GET_TODAY_MESSAGES}?driverId=${driverId}`
      : config.ENDPOINTS.MESSAGES.GET_TODAY_MESSAGES;
    const response = await apiClient.get(url);
    return response.data;
  },

  getAllMessages: async (driverId) => {
    const response = await apiClient.get(`${config.ENDPOINTS.MESSAGES.GET_ALL_MESSAGES}?driverId=${driverId}`);
    return response.data;
  },

  getBroadcastMessages: async () => {
    const response = await apiClient.get(config.ENDPOINTS.MESSAGES.GET_BROADCAST_MESSAGES);
    return response.data;
  },

  getUnread: async () => {
    const response = await apiClient.get(config.ENDPOINTS.MESSAGES.GET_UNREAD);
    return response.data;
  },

  markAsRead: async (messageIds) => {
    const ids = Array.isArray(messageIds) ? messageIds : [messageIds];
    const response = await apiClient.post(config.ENDPOINTS.MESSAGES.MARK_READ, ids);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await apiClient.get(config.ENDPOINTS.MESSAGES.GET_UNREAD_COUNT);
    return response.data;
  }
};

// User API
export const userAPI = {
  getProfile: async () => {
    const response = await apiClient.get(config.ENDPOINTS.USER.GET_PROFILE);
    return response.data;
  },

  isAdmin: async (userId) => {
    const response = await apiClient.get(config.ENDPOINTS.USER.IS_ADMIN, {
      params: { userId }
    });
    return response.data;
  },

  fireDriver: async (driverId) => {
    const response = await apiClient.post(config.ENDPOINTS.USER.FIRE_DRIVER, driverId, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  },

  fireDispatcher: async (dispatcherId) => {
    const response = await apiClient.post(config.ENDPOINTS.USER.FIRE_DISPATCHER, dispatcherId, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  },

  getFiredWorkers: async () => {
    const response = await apiClient.get(config.ENDPOINTS.USER.GET_FIRED_WORKERS);
    return response.data;
  },

  updatePassword: async (userId, oldPassword, newPassword) => {
    const response = await apiClient.post(config.ENDPOINTS.USER.UPDATE_PASSWORD, {
      userId,
      userType: 'dispatcher',
      oldPassword,
      newPassword
    });
    return response.data;
  },

  forgotPassword: async (userId, userType = 'dispatcher') => {
    const response = await apiClient.post(config.ENDPOINTS.USER.FORGOT_PASSWORD, {
      userId,
      userType
    });
    return response.data;
  }
};

// Dashboard API
export const dashboardAPI = {
  getAll: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.GET_ALL);
    return response.data;
  },

  getAssignedRides: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.ASSIGNED_RIDES);
    return response.data;
  },

  getOpenRides: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.OPEN_RIDES);
    return response.data;
  },

  getRidesInProgress: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.RIDES_IN_PROGRESS);
    return response.data;
  },

  getRecurringRidesThisWeek: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.RECURRING_RIDES_THIS_WEEK);
    return response.data;
  },

  getTodaysRides: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.TODAYS_RIDES);
    return response.data;
  },

  getTodaysRecurringRides: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.TODAYS_RECURRING_RIDES);
    return response.data;
  },

  getActiveDrivers: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.ACTIVE_DRIVERS);
    return response.data;
  },

  getOfflineDrivers: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.OFFLINE_DRIVERS);
    return response.data;
  },

  getCompletedUnreviewedRides: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.COMPLETED_UNREVIEWED);
    return response.data;
  },

  markRideAsReviewed: async (rideId) => {
    const response = await apiClient.post(config.ENDPOINTS.DASHBOARD.MARK_AS_REVIEWED, rideId, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  },

  /**
   * Get all drivers with their online status (for admin tab).
   * Returns drivers with IsOnline, HasHeartbeat, and IsAvailable flags.
   */
  getAllDriversWithStatus: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.ALL_DRIVERS_WITH_STATUS);
    return response.data;
  },

  getDriversOnJob: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.DRIVERS_ON_JOB);
    return response.data;
  },

  getUnsettledDrivers: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.UNSETTLED_DRIVERS);
    return response.data;
  },

  getFutureRides: async () => {
    const response = await apiClient.get(config.ENDPOINTS.DASHBOARD.FUTURE_RIDES);
    return response.data;
  },

  getUnsettledRidesByDriver: async (driverId) => {
    const response = await apiClient.get(`${config.ENDPOINTS.RIDES.UNSETTLED_RIDES_BY_DRIVER}/${driverId}`);
    return response.data;
  },

  settleUpDriver: async (driverId) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.SETTLE_UP_DRIVER, driverId, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  },

  settleRide: async (rideId) => {
    const response = await apiClient.post(config.ENDPOINTS.RIDES.SETTLE_RIDE, rideId, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  }
};

// Invoices API
export const invoicesAPI = {
  getDriversWithInvoices: async () => {
    const response = await apiClient.get(config.ENDPOINTS.INVOICES.GET_DRIVERS_WITH_INVOICES);
    return response.data;
  },

  getDriverInvoices: async (driverId) => {
    const response = await apiClient.get(`${config.ENDPOINTS.INVOICES.GET_DRIVER_INVOICES}/${driverId}`);
    return response.data;
  },

  downloadInvoice: async (invoiceNumber) => {
    const response = await apiClient.get(
      `${config.ENDPOINTS.INVOICES.DOWNLOAD}/${invoiceNumber}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  resendInvoice: async (invoiceNumber) => {
    const response = await apiClient.post(config.ENDPOINTS.INVOICES.RESEND, { invoiceNumber });
    return response.data;
  },

  getDownloadUrl: (invoiceNumber) => {
    return `${apiClient.defaults.baseURL}${config.ENDPOINTS.INVOICES.DOWNLOAD}/${invoiceNumber}`;
  }
};

// Payment API
export const paymentAPI = {
  /**
   * Get payment provider configuration from the server.
   * Returns which provider is active (square/stripe) and the public keys needed by client SDKs.
   * @returns {Promise<{provider: string, publishableKey?: string, appId?: string, locationId?: string}>}
   */
  getPaymentConfig: async () => {
    const response = await apiClient.get(config.ENDPOINTS.PAYMENT.CONFIG);
    return response.data;
  },

  /**
   * Converts a short-lived Square nonce (cnon:...) into a permanent card on file (ccof:...).
   * The returned cardId can be stored and charged at any future time without expiry.
   * @param {string} nonce - Square nonce from client-side tokenization
   * @param {string} customerName - Customer's full name for the Square Customer record
   * @returns {Promise<{success: boolean, cardId?: string, last4?: string, cardBrand?: string, message: string}>}
   */
  saveCardOnFile: async (nonce, customerName) => {
    const response = await apiClient.post(config.ENDPOINTS.PAYMENT.SAVE_CARD_ON_FILE, { nonce, customerName });
    return response.data;
  },
};

// Export token manager for use in other modules
export { tokenManager };

// Pricing Admin API
export const pricingAPI = {
  // --- Zip Code Search (for zone creation) ---
  searchZipCodes: async (query, radius = 12) => {
    const params = new URLSearchParams({ query });
    if (radius !== 12) params.append('radius', radius);
    const response = await apiClient.get(`${config.ENDPOINTS.PRICING.ZIP_CODE_SEARCH}?${params}`);
    return response.data;
  },

  resolveZipCodes: async (zips) => {
    const response = await apiClient.get(`${config.ENDPOINTS.PRICING.ZIP_CODE_RESOLVE}?zips=${zips.join(',')}`);
    return response.data; // { "08701": "Lakewood, NJ", ... }
  },

  // --- Zones ---
  getZones: async () => {
    const response = await apiClient.get(config.ENDPOINTS.PRICING.ZONES);
    return response.data;
  },

  getZoneById: async (id) => {
    const response = await apiClient.get(`${config.ENDPOINTS.PRICING.ZONE_BY_ID}/${id}`);
    return response.data;
  },

  createZone: async (zone) => {
    const response = await apiClient.post(config.ENDPOINTS.PRICING.ZONES, zone);
    return response.data;
  },

  updateZone: async (id, zone) => {
    const response = await apiClient.put(`${config.ENDPOINTS.PRICING.ZONE_BY_ID}/${id}`, zone);
    return response.data;
  },

  deleteZone: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.PRICING.ZONE_BY_ID}/${id}`);
    return response.data;
  },

  // --- Route Prices ---
  getRoutes: async (originZoneId, destinationZoneId) => {
    const params = new URLSearchParams();
    if (originZoneId) params.append('originZoneId', originZoneId);
    if (destinationZoneId) params.append('destinationZoneId', destinationZoneId);
    const qs = params.toString();
    const response = await apiClient.get(`${config.ENDPOINTS.PRICING.ROUTES}${qs ? '?' + qs : ''}`);
    return response.data;
  },

  createRoutes: async (request) => {
    // request: { originZoneId, destinationZoneId, createBothDirections, prices: [{carType, price, ownerCut}] }
    const response = await apiClient.post(config.ENDPOINTS.PRICING.ROUTES, request);
    return response.data;
  },

  addSingleRoutePrice: async (originZoneId, destinationZoneId, carType, price, ownerCut) => {
    const response = await apiClient.post(config.ENDPOINTS.PRICING.ROUTES_SINGLE, { originZoneId, destinationZoneId, carType, price, ownerCut });
    return response.data;
  },

  updateRoutePrice: async (id, price, ownerCut) => {
    const response = await apiClient.put(`${config.ENDPOINTS.PRICING.ROUTE_BY_ID}/${id}`, { price, ownerCut });
    return response.data;
  },

  updateRoutePriceBoth: async (id, price, ownerCut) => {
    const response = await apiClient.put(`${config.ENDPOINTS.PRICING.ROUTES_UPDATE_BOTH}/${id}`, { price, ownerCut });
    return response.data;
  },

  deleteRoutes: async (originZoneId, destinationZoneId) => {
    const response = await apiClient.delete(
      `${config.ENDPOINTS.PRICING.ROUTES}?originZoneId=${originZoneId}&destinationZoneId=${destinationZoneId}`
    );
    return response.data;
  },

  // --- Formula Settings ---
  getFormulaSettings: async () => {
    const response = await apiClient.get(config.ENDPOINTS.PRICING.FORMULA_SETTINGS);
    return response.data;
  },

  updateFormulaSettings: async (settings) => {
    const response = await apiClient.put(config.ENDPOINTS.PRICING.FORMULA_SETTINGS, settings);
    return response.data;
  },

  // --- Zone Reorder ---
  reorderZones: async (items) => {
    const response = await apiClient.put(config.ENDPOINTS.PRICING.ZONES_REORDER, items);
    return response.data;
  },

  // --- Rush Hour Fees ---
  getRushHourFees: async () => {
    const response = await apiClient.get(config.ENDPOINTS.PRICING.RUSH_HOUR_FEES);
    return response.data;
  },

  createRushHourFee: async (fee) => {
    const response = await apiClient.post(config.ENDPOINTS.PRICING.RUSH_HOUR_FEES, fee);
    return response.data;
  },

  updateRushHourFee: async (id, fee) => {
    const response = await apiClient.put(`${config.ENDPOINTS.PRICING.RUSH_HOUR_FEES}/${id}`, fee);
    return response.data;
  },

  deleteRushHourFee: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.PRICING.RUSH_HOUR_FEES}/${id}`);
    return response.data;
  },
};

// Company Settings API
export const settingsAPI = {
  get: async () => {
    const response = await apiClient.get(config.ENDPOINTS.SETTINGS.GET);
    return response.data;
  },

  update: async (settings) => {
    const response = await apiClient.put(config.ENDPOINTS.SETTINGS.UPDATE, settings);
    return response.data;
  },
};

// Car Types API
export const carTypesAPI = {
  getAll: async () => {
    const response = await apiClient.get(config.ENDPOINTS.CAR_TYPES.GET_ALL);
    return response.data;
  },

  getAllIncludingInactive: async () => {
    const response = await apiClient.get(config.ENDPOINTS.CAR_TYPES.GET_ALL_INCLUDING_INACTIVE);
    return response.data;
  },

  create: async (request) => {
    // request: { name, sortOrder, defaultSeats, canTakeCallsFor, formulaPerMileRate, routePrices: [{originZoneId, destinationZoneId, price, ownerCut}] }
    const response = await apiClient.post(config.ENDPOINTS.CAR_TYPES.CREATE, request);
    return response.data;
  },

  update: async (id, carType) => {
    // carType: { name, sortOrder, defaultSeats, canTakeCallsFor }
    const response = await apiClient.put(`${config.ENDPOINTS.CAR_TYPES.UPDATE}/${id}`, carType);
    return response.data;
  },

  deactivate: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.CAR_TYPES.DEACTIVATE}/${id}`);
    return response.data;
  },

  reactivate: async (id) => {
    const response = await apiClient.post(`${config.ENDPOINTS.CAR_TYPES.REACTIVATE}/${id}`);
    return response.data;
  },

  checkPricingComplete: async () => {
    const response = await apiClient.get(config.ENDPOINTS.CAR_TYPES.PRICING_COMPLETE);
    return response.data;
  },

  getRoutePairs: async () => {
    const response = await apiClient.get(config.ENDPOINTS.CAR_TYPES.ROUTE_PAIRS);
    return response.data;
  },
};

// Email Templates API
export const emailTemplatesAPI = {
  getAll: async () => {
    const response = await apiClient.get(config.ENDPOINTS.EMAIL_TEMPLATES.GET_ALL);
    return response.data;
  },

  getByKey: async (key) => {
    const response = await apiClient.get(`${config.ENDPOINTS.EMAIL_TEMPLATES.GET_BY_KEY}/${key}`);
    return response.data;
  },

  update: async (key, subject, bodyHtml) => {
    const response = await apiClient.put(`${config.ENDPOINTS.EMAIL_TEMPLATES.UPDATE}/${key}`, { subject, bodyHtml });
    return response.data;
  },

  reset: async (key) => {
    const response = await apiClient.post(`${config.ENDPOINTS.EMAIL_TEMPLATES.RESET}/${key}/reset`);
    return response.data;
  },

  preview: async (key, subject, bodyHtml) => {
    const response = await apiClient.post(`${config.ENDPOINTS.EMAIL_TEMPLATES.PREVIEW}/${key}/preview`, { subject, bodyHtml });
    return response.data;
  },

  sendTest: async (key, toEmail, subject, bodyHtml) => {
    const response = await apiClient.post(`${config.ENDPOINTS.EMAIL_TEMPLATES.SEND_TEST}/${key}/send-test`, { toEmail, subject, bodyHtml });
    return response.data;
  },
};

// Add-Ons API
export const addOnsAPI = {
  getActive: async () => {
    const response = await apiClient.get(config.ENDPOINTS.ADD_ONS.GET_ACTIVE);
    return response.data;
  },

  getAll: async () => {
    const response = await apiClient.get(config.ENDPOINTS.ADD_ONS.GET_ALL);
    return response.data;
  },

  create: async (addOn) => {
    // addOn: { name, fee, sortOrder }
    const response = await apiClient.post(config.ENDPOINTS.ADD_ONS.CREATE, addOn);
    return response.data;
  },

  update: async (id, addOn) => {
    // addOn: { name, fee, sortOrder }
    const response = await apiClient.put(`${config.ENDPOINTS.ADD_ONS.UPDATE}/${id}`, addOn);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.ADD_ONS.DELETE}/${id}`);
    return response.data;
  },

  reactivate: async (id) => {
    const response = await apiClient.post(`${config.ENDPOINTS.ADD_ONS.REACTIVATE}/${id}/reactivate`);
    return response.data;
  },
};

export const accountsAPI = {
  getAll: async () => {
    const response = await apiClient.get(config.ENDPOINTS.ACCOUNTS.GET_ALL);
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`${config.ENDPOINTS.ACCOUNTS.GET_BY_ID}/${id}`);
    return response.data;
  },

  search: async (q) => {
    const response = await apiClient.get(config.ENDPOINTS.ACCOUNTS.SEARCH, { params: { q } });
    return response.data;
  },

  create: async (account) => {
    const response = await apiClient.post(config.ENDPOINTS.ACCOUNTS.CREATE, account);
    return response.data;
  },

  update: async (id, account) => {
    const response = await apiClient.put(`${config.ENDPOINTS.ACCOUNTS.UPDATE}/${id}`, account);
    return response.data;
  },

  deactivate: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.ACCOUNTS.DEACTIVATE}/${id}`);
    return response.data;
  },

  reactivate: async (id) => {
    const response = await apiClient.post(`${config.ENDPOINTS.ACCOUNTS.REACTIVATE}/${id}/reactivate`);
    return response.data;
  },

  attachCard: async (id, nonce, customerName) => {
    const response = await apiClient.post(`${config.ENDPOINTS.ACCOUNTS.ATTACH_CARD}/${id}/card`, { nonce, customerName });
    return response.data;
  },

  removeCard: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.ACCOUNTS.REMOVE_CARD}/${id}/card`);
    return response.data;
  },
};

export const agreementsAPI = {
  getAll: async () => {
    const response = await apiClient.get(config.ENDPOINTS.AGREEMENTS.GET_ALL);
    return response.data;
  },

  upload: async (title, file, requireSignature = true, sortOrder = 0) => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('requireSignature', String(requireSignature));
    formData.append('sortOrder', String(sortOrder));
    formData.append('file', file);
    const response = await apiClient.post(config.ENDPOINTS.AGREEMENTS.UPLOAD, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateMeta: async (id, meta) => {
    const response = await apiClient.put(`${config.ENDPOINTS.AGREEMENTS.UPDATE_META}/${id}/meta`, meta);
    return response.data;
  },

  replacePdf: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.put(`${config.ENDPOINTS.AGREEMENTS.REPLACE_PDF}/${id}/pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteForm: async (id) => {
    const response = await apiClient.delete(`${config.ENDPOINTS.AGREEMENTS.DELETE}/${id}`);
    return response.data;
  },

  downloadBlank: async (id) => {
    const response = await apiClient.get(`${config.ENDPOINTS.AGREEMENTS.DOWNLOAD_BLANK}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getSignedForDriver: async (driverId) => {
    const response = await apiClient.get(`${config.ENDPOINTS.AGREEMENTS.GET_SIGNED}/${driverId}`);
    return response.data;
  },

  downloadSigned: async (driverId, signedId) => {
    const response = await apiClient.get(
      `${config.ENDPOINTS.AGREEMENTS.DOWNLOAD_SIGNED}/${driverId}/${signedId}/pdf`,
      { responseType: 'blob' }
    );
    return response.data;
  },
};